import { t } from 'i18next';
import * as yup from 'yup';

import { insightsEndpoints } from '@/api/endpoints';
import type { ValidateInsightBody } from '@/shared/types/authoring-validation';
import { Insight, INSIGHT_TYPES } from '@/shared/types/insights';
import { LENGTH_S } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

interface InsightDraft {
  name?: string;
  description?: string;
  type?: string;
  categories?: { value?: string }[];
}

/**
 * Builds the advisory `/insights/validate` body, or `null` when the draft has no content yet.
 * `type` is excluded from the emptiness check: it defaults to STRING and is never cleared.
 */
export const buildInsightValidateBody = (draft: InsightDraft): ValidateInsightBody | null => {
  const categories = (draft.categories ?? [])
    .map((cat) => cat?.value ?? '')
    .filter((category) => category.trim() !== '');
  const name = draft.name?.trim();
  const description = draft.description?.trim();

  if (!name && !description && !categories.length) {
    return null;
  }

  return {
    name: name || undefined,
    description: description || undefined,
    type: (draft.type || undefined) as Insight['type'] | undefined,
    categories:
      draft.type === INSIGHT_TYPES.CATEGORICAL && categories.length ? categories : undefined,
  };
};

/**
 * Backend slug pattern validation - matches /^\w[\w_]*$/i
 * Allows alphanumeric characters and underscores, but must start with a word character
 */
const INSIGHT_PATTERN = /^\w[\w_]*$/i;

/**
 * Converts a string into a slug that matches the INSIGHT_PATTERN: /^\w[\w_]*$/i
 */
export const convertToValidInsight = (value: string): string => {
  return value
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^\w\s_]/g, '') // Remove all non-word, non-space, non-underscore characters
    .replace(/^_+/, ''); // Remove leading underscores
};

/**
 * Validates that a string matches the slug pattern used by the backend
 * @param value - The string to validate
 * @returns boolean indicating if the value matches the slug pattern
 */
const isValidInsight = (value: string): boolean => {
  return INSIGHT_PATTERN.test(value);
};

export const createInsightSchema = (brainId: string, currentName?: string) =>
  yup.object().shape({
    name: createNameFieldSchema({
      max: LENGTH_S,
      getEndpoint: (name) => insightsEndpoints.checkName(brainId, name, { brain_id: brainId }),
      excludeName: currentName,
    }).test('slug-format', t('validation.slug_format'), (value) => {
      if (!value) {
        return true;
      }
      return isValidInsight(value);
    }),
    // `.trim()` transforms, so the resolver validates and submits the same string. Without it
    // `.min(16)` accepts 16 spaces and the server rejects it on word count.
    description: yup
      .string()
      .label(t('ai_agents.insight'))
      .trim()
      .required(t('validation.required'))
      .min(16, t('validation.min_length', { 0: 16 }))
      .max(2048, t('validation.max_length', { 0: 2048 })),
    type: yup
      .string()
      .label(t('ai_agents.insight_type'))
      .required(t('validation.required'))
      .oneOf(Object.values(INSIGHT_TYPES), t('validation.invalid_selection')),
    categories: yup.array().when('type', {
      is: INSIGHT_TYPES.CATEGORICAL,
      then: (schema) =>
        schema
          .of(
            yup.object({
              value: yup
                .string()
                .max(64, t('validation.max_length', { 0: 64 }))
                .test('slug-format', t('validation.slug_format'), (value) => {
                  if (!value) {
                    return true;
                  }
                  return isValidInsight(value);
                }),
            })
          )
          .required(t('validation.required'))
          .min(1, t('validation.min_items', { 0: 1 }))
          .max(10, t('validation.max_items', { 0: 10 }))
          // Blank rows are dropped on submit, so a blank-only list would save as `[]`.
          .test('non-empty-category', t('insights.category_required'), (categories) =>
            (categories ?? []).some((category) => Boolean(category?.value?.trim()))
          )
          .test('unique-categories', t('validation.unique_items'), function (categories) {
            const seen = new Set<string>();
            const duplicates: yup.ValidationError[] = [];

            // Flag each repeat on its own row; blank rows are skipped but keep their index.
            (categories ?? []).forEach((category, index) => {
              const value = category?.value?.toLowerCase().trim();
              if (!value) {
                return;
              }
              if (seen.has(value)) {
                duplicates.push(
                  this.createError({
                    path: `categories[${index}].value`,
                    message: t('validation.duplicate_category'),
                  })
                );
              }
              seen.add(value);
            });

            return duplicates.length ? new yup.ValidationError(duplicates) : true;
          }),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
